/**
 * ImageSettings - настройки изображения с поддержкой загрузки и медиа-библиотеки
 * Переиспользуется везде где нужно изображение
 *
 * Поддерживает как строку (URL), так и объект:
 * { src, alt, caption, storagePath }
 */
import React, { useState, useRef } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { uploadImage } from '../../../services/storage';
import MediaLibraryModal from './MediaLibraryModal';

// Хелпер для нормализации значения (строка → объект)
export const getImageData = (value) => {
  if (!value) return { src: '' };
  if (typeof value === 'object' && value !== null) return value;
  return { src: value };
};

// Хелпер для получения src из значения (для рендер-компонентов)
export const getImageSrc = (value) => {
  if (!value) return '';
  if (typeof value === 'object' && value !== null) return value.src || '';
  return value;
};

export default function ImageSettings({
  label = 'Изображение',
  value,                          // string | { src, alt, caption, storagePath }
  onChange,
  placeholder = 'https://example.com/image.jpg',

  // Превью
  showPreview = true,
  previewHeight = 120,

  // Feature flags
  showAlt = false,
  showCaption = false,
  showUpload = false,             // Фаза 2: загрузка в Storage
  showMediaLibrary = false,       // Фаза 3: выбор из библиотеки

  // Контекст
  defaults = {},                  // Значения по умолчанию
  hint,

  // Upload context (для Фазы 2)
  projectId,
  onMediaLibraryOpen,             // Фаза 3: callback для открытия библиотеки
}) {
  const [showOptions, setShowOptions] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [showMediaLibraryModal, setShowMediaLibraryModal] = useState(false);
  const fileInputRef = useRef(null);

  // Нормализуем значение
  const imageData = getImageData(value);
  const src = imageData.src || '';
  const alt = imageData.alt ?? defaults.alt ?? '';
  const caption = imageData.caption ?? defaults.caption ?? '';

  const hasOptions = showAlt || showCaption;
  const hasCustomOptions = imageData.alt || imageData.caption;

  // Обновление поля
  const updateField = (field, newValue) => {
    // Если нет опций и обновляем src, возвращаем строку (обратная совместимость)
    if (!hasOptions && field === 'src') {
      onChange(newValue);
      return;
    }

    // Иначе возвращаем объект
    onChange({
      ...imageData,
      [field]: newValue,
    });
  };

  // Очистка изображения
  const clearImage = () => {
    if (!hasOptions) {
      onChange('');
    } else {
      onChange({ ...imageData, src: '', storagePath: undefined });
    }
    setImageError(false);
  };

  // Обработка загрузки файла
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Проверяем что есть projectId
    if (!projectId) {
      setUploadError('Сначала сохраните сайт');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    setImageError(false);

    try {
      const result = await uploadImage(file, projectId, (progress) => {
        setUploadProgress(progress);
      });

      // Обновляем значение с URL и путём в Storage
      const newValue = hasOptions
        ? {
            ...imageData,
            src: result.url,
            storagePath: result.path,
          }
        : result.url;

      onChange(newValue);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Ошибка загрузки');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Обработка выбора из медиа-библиотеки
  const handleMediaSelect = (selectedMedia) => {
    setShowMediaLibraryModal(false);
    setImageError(false);

    const newValue = hasOptions
      ? {
          ...imageData,
          src: selectedMedia.src,
          storagePath: selectedMedia.storagePath,
        }
      : selectedMedia.src;

    onChange(newValue);
  };

  // Открытие медиа-библиотеки
  const openMediaLibrary = () => {
    if (onMediaLibraryOpen) {
      // Используем внешний callback если передан
      onMediaLibraryOpen();
    } else {
      // Иначе открываем встроенную модалку
      setShowMediaLibraryModal(true);
    }
  };

  return (
    <div className="space-y-2">
      {/* Заголовок с кнопкой опций */}
      {label && (
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium">{label}</label>
          {hasOptions && (
            <button
              onClick={() => setShowOptions(!showOptions)}
              className={`flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
                showOptions || hasCustomOptions
                  ? 'bg-primary/10 text-primary'
                  : 'text-text-secondary hover:bg-muted'
              }`}
              title="Дополнительные настройки"
            >
              <Icon name="Settings2" size={12} />
              <span>Опции</span>
              <Icon name={showOptions ? 'ChevronUp' : 'ChevronDown'} size={12} />
            </button>
          )}
        </div>
      )}

      {/* Панель опций */}
      {hasOptions && showOptions && (
        <div className="space-y-2 p-2 bg-muted rounded-lg">
          {showAlt && (
            <Input
              value={alt}
              onChange={(e) => updateField('alt', e.target.value)}
              placeholder="Alt текст (описание для доступности)"
            />
          )}
          {showCaption && (
            <Input
              value={caption}
              onChange={(e) => updateField('caption', e.target.value)}
              placeholder="Подпись к изображению"
            />
          )}
        </div>
      )}

      {/* Превью изображения */}
      {showPreview && src && !imageError && (
        <div
          className="relative rounded-lg overflow-hidden bg-muted border border-border"
          style={{ height: previewHeight }}
        >
          <img
            src={src}
            alt={alt || 'Preview'}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
          <button
            onClick={clearImage}
            className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/70 rounded text-white transition-colors"
            title="Удалить изображение"
          >
            <Icon name="X" size={14} />
          </button>
        </div>
      )}

      {/* Ошибка загрузки превью */}
      {showPreview && src && imageError && (
        <div
          className="relative rounded-lg overflow-hidden bg-muted border border-border flex items-center justify-center"
          style={{ height: previewHeight }}
        >
          <div className="text-center text-text-secondary">
            <Icon name="ImageOff" size={24} className="mx-auto mb-1" />
            <p className="text-xs">Не удалось загрузить</p>
          </div>
          <button
            onClick={clearImage}
            className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/70 rounded text-white transition-colors"
          >
            <Icon name="X" size={14} />
          </button>
        </div>
      )}

      {/* Прогресс загрузки */}
      {uploading && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-text-secondary">
            <span>Загрузка...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Ошибка загрузки */}
      {uploadError && (
        <div className="flex items-center gap-2 p-2 bg-error/10 text-error rounded-lg text-xs">
          <Icon name="AlertCircle" size={14} />
          <span>{uploadError}</span>
          <button
            onClick={() => setUploadError(null)}
            className="ml-auto p-0.5 hover:bg-error/20 rounded"
          >
            <Icon name="X" size={12} />
          </button>
        </div>
      )}

      {/* URL input */}
      <Input
        value={src}
        onChange={(e) => {
          updateField('src', e.target.value);
          setImageError(false);
        }}
        placeholder={placeholder}
      />

      {/* Кнопки действий (Загрузка / Библиотека) */}
      {(showUpload || showMediaLibrary) && (
        <div className="flex gap-2">
          {showUpload && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                <Icon name="Upload" size={14} />
                <span>Загрузить</span>
              </button>
            </>
          )}
          {showMediaLibrary && (
            <button
              onClick={openMediaLibrary}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="Images" size={14} />
              <span>Библиотека</span>
            </button>
          )}
        </div>
      )}

      {/* Модальное окно медиа-библиотеки */}
      {showMediaLibraryModal && projectId && (
        <MediaLibraryModal
          projectId={projectId}
          onSelect={handleMediaSelect}
          onClose={() => setShowMediaLibraryModal(false)}
        />
      )}

      {/* Подсказка */}
      {hint && (
        <p className="text-xs text-text-secondary">{hint}</p>
      )}
    </div>
  );
}

// Экспорт хелперов для использования в других местах
ImageSettings.getImageData = getImageData;
ImageSettings.getImageSrc = getImageSrc;
